/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1904279553 — Відновлення сесії після перезавантаження */

import { useState } from 'react';

import { useAppStore } from '../../../shared/hooks/useAppStore';
import { useTimer } from '../../../shared/hooks/useTimer';
import { debug, getVmDetails } from '../../../shared/lib/general';
import { getOrderInfo } from '../api/order.api';
import { persistOrderIdInUrl } from '../lib/persistOrderIdInUrl';
import { useConnectToVendingMachine } from './useConnectToVendingMachine';

interface UseConnectingHandshakeReturn {
    isConnected: boolean;
    orderId: string;
    setIsConnected: (v: boolean) => void;
    recoverFromOid: (oid: string) => Promise<boolean>;
    connect: (onFailure: (error: unknown) => void) => void;
}

/**
 * Owns the Connecting-screen-local state (isConnected + orderId) and the
 * two-way entry into the apparatus session:
 *
 *   - `connect(onFailure)`   — issue /api/connect, persist the returned order
 *                              id into the URL (?oid=) so a reload survives,
 *                              short-circuit `isConnected` for CT10/CT5 paths
 *   - `recoverFromOid(oid)`  — try to revive a previous session by fetching
 *                              the order from the URL `?oid=`; returns true
 *                              if the order is valid and belongs to this vm
 *
 * The Connecting screen wires these into its mount useEffect, plus uses
 * `setIsConnected` from the polling hook when the apparatus eventually acks.
 */
export const useConnectingHandshake = (
    timer: ReturnType<typeof useTimer>,
): UseConnectingHandshakeReturn => {
    const { mutateAsync: connectToVM } = useConnectToVendingMachine();
    const vm = useAppStore((s) => s.vm);
    const nfc = useAppStore((s) => s.nfc);
    const qrCode = useAppStore((s) => s.qrCode);
    const setOrder = useAppStore((s) => s.setOrder);

    const [isConnected, setIsConnected] = useState<boolean>(false);
    const [orderId, setOrderId] = useState<string>('');

    const { isCT10, isCT5, isConnected: hasConnection } = getVmDetails(vm);

    const recoverFromOid = async (oid: string): Promise<boolean> => {
        try {
            const orderInfo = await getOrderInfo(oid, timer.time);
            if (!orderInfo?.id) return false;
            if (orderInfo.vm_id !== vm.vm_id) return false;
            if (orderInfo.status > 1) return false;

            debug(`Recovered order ${oid}`, 'info', orderInfo);
            setOrderId(String(orderInfo.id));
            setOrder(orderInfo);

            if (isCT10 || (isCT5 && hasConnection)) {
                setIsConnected(true);
            }
            return true;
        } catch (e: unknown) {
            debug('Recovery getOrderInfo failed', 'warn', e);
            return false;
        }
    };

    const connect = (onFailure: (error: unknown) => void): void => {
        debug(`VM: ${vm.vm_id} Order: ${orderId} QR: ${qrCode}`, 'info');
        if (!qrCode) {
            debug('☠️ QR code is not set', 'error');
            onFailure('QR code is not set');
            return;
        }
        if (!vm?.qr || vm.qr === 'qr') {
            debug('☠️ VM is not set', 'error');
            onFailure('VM is not set');
            return;
        }
        if (isConnected) return;

        debug('🚧 Connect to VM', 'warn');
        connectToVM(
            { qr: vm.qr, nfc, timerTime: timer.time },
            {
                onSuccess: (result) => {
                    setOrderId(result.id as string);
                    setOrder(result);

                    if (result.id) persistOrderIdInUrl(result.id);

                    // CT10 / CT5-with-connection don't need to wait for an
                    // explicit MQTT ack — short-circuit isConnected.
                    if (isCT10 || (isCT5 && hasConnection)) {
                        setIsConnected(true);
                    }
                },
                onError: (error) => {
                    debug('Connect to VM failed:', 'error', error);
                    setIsConnected(false);
                    onFailure(error);
                },
            },
        );
    };

    return { isConnected, orderId, setIsConnected, recoverFromOid, connect };
};
